import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Mail, Inbox, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jly-admin-token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setInquiries([]);
  }, []);

  const fetchInquiries = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setInquiries(data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchInquiries(token);
  }, [token, fetchInquiries]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink text-background px-6 dotted-grid">
        <motion.form
          onSubmit={login}
          data-testid="admin-login-form"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm rounded-3xl border border-background/15 bg-background/5 backdrop-blur-xl p-8"
        >
          <span className="grid place-items-center h-12 w-12 rounded-full bg-accent-warm text-ink">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-medium tracking-tight">Admin access</h1>
          <p className="mt-2 text-sm text-background/60">Enter your password to view inquiries.</p>

          <div className="mt-6 space-y-2">
            <Label htmlFor="pw" className="text-background/80">Password</Label>
            <Input
              id="pw"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-background/10 border-background/20 text-background placeholder:text-background/40"
            />
          </div>

          <Button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full h-11 bg-accent-warm text-ink hover:bg-accent-warm/90"
          >
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
          </Button>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-4 w-full text-center text-xs text-background/50 hover:text-background/80 transition-colors"
          >
            ← Back to site
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-foreground">
      <header className="border-b border-border bg-background/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground font-display font-semibold">J</span>
            <span className="font-display font-medium tracking-tight">Inquiries</span>
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold" data-testid="admin-count">{inquiries.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchInquiries(token)} data-testid="admin-refresh-button" className="rounded-full gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button" className="rounded-full gap-2">
              <LogOut className="h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        {loading && inquiries.length === 0 ? (
          <div className="grid place-items-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="grid place-items-center py-24 text-center" data-testid="admin-empty">
            <span className="grid place-items-center h-14 w-14 rounded-full bg-muted text-muted-foreground">
              <Inbox className="h-6 w-6" />
            </span>
            <p className="mt-4 font-display text-lg font-medium">No inquiries yet</p>
            <p className="text-sm text-muted-foreground">New contact submissions will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-inquiries-list">
            {inquiries.map((q) => (
              <article key={q.id} data-testid="admin-inquiry-card" className="rounded-2xl border border-border bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-medium">{q.name}</h3>
                    <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                      <Mail className="h-3.5 w-3.5" /> {q.email}
                    </a>
                  </div>
                  <div className="text-right">
                    {q.service && (
                      <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">{q.service}</span>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">{fmtDate(q.created_at)}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">{q.message}</p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
